/**
 * QrCodesTab — card grid of the target user's dynamic QR codes plus the
 * create/edit modal.
 *
 * Every code encodes fhl.one/q/{short_code} (the tracking redirect), so the
 * DESTINATION stays editable here even after the code is printed — that's the
 * whole point of the wizard allowing destination changes on existing codes.
 * Downloads: print-resolution PNG (1024px) and vector SVG.
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Tag,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiMoreVertical,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiDownload,
  FiUpload,
  FiExternalLink,
} from 'react-icons/fi';
import { firebase } from '../../../utils/init-firebase';
import QrImage from './QrImage';
import {
  call,
  shortLinkUrl,
  downloadQrPng,
  downloadQrSvg,
  slugifyName,
} from './qrStudioUtils';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const EMPTY_DRAFT = {
  id: null,
  label: '',
  destinationType: 'form',
  formId: '',
  bookingPageId: '',
  destinationUrl: '',
  design: { darkColor: '#1A365D', lightColor: '#FFFFFF', logoUrl: '' },
  isActive: true,
};

function destinationSummary(qr, forms, bookingPages) {
  if (qr.destination_type === 'form') {
    const form = forms.find((f) => f.id === qr.form_id);
    return { kind: 'Form', detail: form ? form.title : 'Deleted form', missing: !form };
  }
  if (qr.destination_type === 'booking_page') {
    const page = bookingPages.find((p) => p.id === qr.booking_page_id);
    return { kind: 'Booking', detail: page ? page.title : 'Deleted page', missing: !page };
  }
  try {
    return { kind: 'URL', detail: new URL(qr.destination_url).hostname, missing: false };
  } catch (e) {
    return { kind: 'URL', detail: qr.destination_url || '—', missing: !qr.destination_url };
  }
}

function QrWizardModal({ isOpen, onClose, targetUserId, draft, setDraft, forms, bookingPages, onSaved }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);
  const isEdit = !!draft.id;

  // Preview encodes the real short link when editing; a representative one when creating
  const previewText = isEdit && draft.shortCode ? shortLinkUrl(draft.shortCode) : 'https://fhl.one/q/preview1';

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setDesign = (patch) => setDraft((prev) => ({ ...prev, design: { ...prev.design, ...patch } }));

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Use PNG, JPEG, or WebP.', status: 'warning', duration: 3000 });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: 'File too large', description: 'Logos must be under 5 MB.', status: 'warning', duration: 3000 });
      return;
    }
    setUploading(true);
    try {
      const path = `qr-codes/${targetUserId}/${Date.now()}_${file.name}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(file);
      const downloadUrl = await storageRef.getDownloadURL();
      setDesign({ logoUrl: downloadUrl });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!draft.label.trim()) {
      toast({ title: 'Give the QR code a label', description: 'e.g. "Spring Home Expo table"', status: 'warning', duration: 3000 });
      return;
    }
    if (draft.destinationType === 'form' && !draft.formId) {
      toast({ title: 'Pick a form destination', status: 'warning', duration: 3000 });
      return;
    }
    if (draft.destinationType === 'booking_page' && !draft.bookingPageId) {
      toast({ title: 'Pick a booking page destination', status: 'warning', duration: 3000 });
      return;
    }
    if (draft.destinationType === 'url' && !/^https?:\/\//i.test(draft.destinationUrl.trim())) {
      toast({ title: 'Enter a valid destination URL', description: 'Must start with https://', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    try {
      const result = await call('manageQrCodes', {
        userId: targetUserId,
        action: 'save',
        qr: {
          id: draft.id || undefined,
          label: draft.label.trim(),
          destinationType: draft.destinationType,
          formId: draft.destinationType === 'form' ? draft.formId : undefined,
          bookingPageId: draft.destinationType === 'booking_page' ? draft.bookingPageId : undefined,
          destinationUrl: draft.destinationType === 'url' ? draft.destinationUrl.trim() : undefined,
          design: draft.design,
          isActive: draft.isActive,
        },
      });
      toast({
        title: isEdit ? 'QR code updated' : 'QR code created',
        description: isEdit ? undefined : 'Download it below or from the card menu.',
        status: 'success',
        duration: 4000,
      });
      onSaved(result.qr);
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit QR Code' : 'New QR Code'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* Left: settings */}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Label</FormLabel>
                <Input
                  value={draft.label}
                  onChange={(e) => set({ label: e.target.value })}
                  placeholder='e.g. "Spring Home Expo table"'
                />
                <FormHelperText>Internal only — helps you tell codes apart.</FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Where should a scan go?</FormLabel>
                <Select
                  value={draft.destinationType}
                  onChange={(e) => set({ destinationType: e.target.value })}
                >
                  <option value="form">One of my lead forms</option>
                  <option value="booking_page">One of my booking pages</option>
                  <option value="url">A custom URL</option>
                </Select>
                {isEdit && (
                  <FormHelperText>
                    Safe to change anytime — printed codes keep working because they point at the tracking link.
                  </FormHelperText>
                )}
              </FormControl>

              {draft.destinationType === 'form' && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Lead form</FormLabel>
                  <Select
                    placeholder={forms.length ? 'Select a form' : 'No forms yet — create one in the Forms tab'}
                    value={draft.formId || ''}
                    onChange={(e) => set({ formId: e.target.value })}
                  >
                    {forms.map((form) => (
                      <option key={form.id} value={form.id}>{form.title}</option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {draft.destinationType === 'booking_page' && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Booking page</FormLabel>
                  <Select
                    placeholder={bookingPages.length ? 'Select a booking page' : 'No active booking pages found'}
                    value={draft.bookingPageId || ''}
                    onChange={(e) => set({ bookingPageId: e.target.value })}
                  >
                    {bookingPages.map((page) => (
                      <option key={page.id} value={page.id}>{page.title}</option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {draft.destinationType === 'url' && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Destination URL</FormLabel>
                  <Input
                    value={draft.destinationUrl}
                    onChange={(e) => set({ destinationUrl: e.target.value })}
                    placeholder="https://..."
                    type="url"
                  />
                </FormControl>
              )}

              <HStack spacing={4} pt={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Code color</FormLabel>
                  <Input
                    type="color"
                    value={draft.design.darkColor}
                    onChange={(e) => setDesign({ darkColor: e.target.value })}
                    w="64px"
                    h="38px"
                    p={1}
                    cursor="pointer"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Background</FormLabel>
                  <Input
                    type="color"
                    value={draft.design.lightColor}
                    onChange={(e) => setDesign({ lightColor: e.target.value })}
                    w="64px"
                    h="38px"
                    p={1}
                    cursor="pointer"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Center logo (optional)</FormLabel>
                <HStack>
                  <input
                    type="file"
                    ref={logoInputRef}
                    accept={ALLOWED_TYPES.join(',')}
                    style={{ display: 'none' }}
                    onChange={handleLogoFile}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={uploading ? <Spinner size="xs" /> : <FiUpload />}
                    onClick={() => logoInputRef.current?.click()}
                    isDisabled={uploading}
                  >
                    {draft.design.logoUrl ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  {draft.design.logoUrl && (
                    <Button size="sm" variant="ghost" colorScheme="red" onClick={() => setDesign({ logoUrl: '' })}>
                      Remove
                    </Button>
                  )}
                </HStack>
                <FormHelperText>Keep it simple — small square logos scan best.</FormHelperText>
              </FormControl>
            </VStack>

            {/* Right: live preview */}
            <VStack spacing={3}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Live preview</Text>
              <Box borderWidth="1px" borderRadius="xl" p={4} bg="white">
                <QrImage text={previewText} design={draft.design} size={220} />
              </Box>
              {isEdit && draft.shortCode && (
                <Text fontSize="xs" color="gray.500" fontFamily="mono">{shortLinkUrl(draft.shortCode)}</Text>
              )}
              <Text fontSize="xs" color="gray.400" textAlign="center" maxW="240px">
                Scans route through the tracking link first, then land on your destination.
              </Text>
            </VStack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
            {isEdit ? 'Save changes' : 'Create QR code'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function QrCodesTab({ targetUserId, qrs, forms, bookingPages, perQr, onChanged }) {
  const toast = useToast();
  const wizard = useDisclosure();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  const openCreate = () => {
    setDraft({ ...EMPTY_DRAFT, design: { ...EMPTY_DRAFT.design } });
    wizard.onOpen();
  };

  const openEdit = (qr) => {
    setDraft({
      id: qr.id,
      shortCode: qr.short_code,
      label: qr.label || '',
      destinationType: qr.destination_type,
      formId: qr.form_id || '',
      bookingPageId: qr.booking_page_id || '',
      destinationUrl: qr.destination_url || '',
      design: {
        darkColor: qr.design?.darkColor || '#1A365D',
        lightColor: qr.design?.lightColor || '#FFFFFF',
        logoUrl: qr.design?.logoUrl || '',
      },
      isActive: qr.is_active !== false,
    });
    wizard.onOpen();
  };

  const copyLink = (qr) => {
    navigator.clipboard.writeText(shortLinkUrl(qr.short_code));
    toast({ title: 'Short link copied', description: shortLinkUrl(qr.short_code), status: 'success', duration: 2500 });
  };

  const handleToggle = async (qr, isActive) => {
    try {
      await call('manageQrCodes', { userId: targetUserId, action: 'toggle_active', qrId: qr.id, isActive });
      onChanged();
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await call('manageQrCodes', { userId: targetUserId, action: 'delete', qrId: pendingDelete.id });
      toast({ title: 'QR code deleted', status: 'success', duration: 3000 });
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (qr, format) => {
    try {
      const base = slugifyName(qr.label) || `qr-${qr.short_code}`;
      if (format === 'png') await downloadQrPng(qr, base);
      else await downloadQrSvg(qr, base);
    } catch (err) {
      toast({ title: 'Download failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={5}>
        <Text color="gray.500" fontSize="sm">
          Every code points at a tracking short link, so you can change its destination after printing.
        </Text>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate} flexShrink={0}>
          New QR Code
        </Button>
      </HStack>

      {qrs.length === 0 ? (
        <Center borderWidth="2px" borderStyle="dashed" borderColor={cardBorder} borderRadius="xl" py={16} flexDir="column">
          <Heading size="sm" color="gray.500" mb={2}>No QR codes yet</Heading>
          <Text color="gray.400" fontSize="sm" mb={4}>Create one for your next event, flyer, or open house.</Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" variant="outline" onClick={openCreate}>
            Create your first QR code
          </Button>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {qrs.map((qr) => {
            const dest = destinationSummary(qr, forms, bookingPages);
            const stats = perQr?.[qr.id];
            return (
              <Box
                key={qr.id}
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="xl"
                p={4}
                opacity={qr.is_active ? 1 : 0.6}
              >
                <HStack align="start" spacing={4}>
                  <Box borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={1.5} bg="white" flexShrink={0}>
                    <QrImage text={shortLinkUrl(qr.short_code)} design={qr.design} size={104} />
                  </Box>
                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Text fontWeight="bold" noOfLines={1}>{qr.label}</Text>
                    <HStack spacing={1.5}>
                      <Tag size="sm" colorScheme={dest.missing ? 'red' : 'blue'}>{dest.kind}</Tag>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>{dest.detail}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.400" fontFamily="mono" noOfLines={1}>
                      fhl.one/q/{qr.short_code}
                    </Text>
                    <HStack spacing={3} pt={1}>
                      <Text fontSize="sm"><b>{qr.scan_count || 0}</b> <Text as="span" color="gray.500" fontSize="xs">scans</Text></Text>
                      <Text fontSize="sm"><b>{stats?.submissions || 0}</b> <Text as="span" color="gray.500" fontSize="xs">submissions</Text></Text>
                    </HStack>
                    {qr.last_scanned_at && (
                      <Text fontSize="xs" color="gray.400">
                        Last scan {new Date(qr.last_scanned_at).toLocaleString()}
                      </Text>
                    )}
                  </VStack>
                </HStack>

                <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px" borderColor={cardBorder}>
                  <HStack>
                    <Tooltip label="Copy short link">
                      <IconButton icon={<FiCopy />} size="sm" variant="ghost" aria-label="Copy link" onClick={() => copyLink(qr)} />
                    </Tooltip>
                    <Tooltip label="Download print-ready PNG">
                      <IconButton icon={<FiDownload />} size="sm" variant="ghost" aria-label="Download PNG" onClick={() => handleDownload(qr, 'png')} />
                    </Tooltip>
                    <Tooltip label="Test the link">
                      <IconButton
                        icon={<FiExternalLink />}
                        size="sm"
                        variant="ghost"
                        aria-label="Open link"
                        onClick={() => window.open(shortLinkUrl(qr.short_code), '_blank')}
                      />
                    </Tooltip>
                  </HStack>
                  <HStack>
                    <Switch
                      size="sm"
                      isChecked={qr.is_active !== false}
                      onChange={(e) => handleToggle(qr, e.target.checked)}
                    />
                    <Menu>
                      <MenuButton as={IconButton} icon={<FiMoreVertical />} size="sm" variant="ghost" aria-label="More" />
                      <MenuList>
                        <MenuItem icon={<FiEdit2 />} onClick={() => openEdit(qr)}>Edit</MenuItem>
                        <MenuItem icon={<FiDownload />} onClick={() => handleDownload(qr, 'png')}>Download PNG</MenuItem>
                        <MenuItem icon={<FiDownload />} onClick={() => handleDownload(qr, 'svg')}>Download SVG</MenuItem>
                        <MenuItem icon={<FiCopy />} onClick={() => copyLink(qr)}>Copy short link</MenuItem>
                        <MenuDivider />
                        <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => setPendingDelete(qr)}>Delete</MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </HStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      <QrWizardModal
        isOpen={wizard.isOpen}
        onClose={wizard.onClose}
        targetUserId={targetUserId}
        draft={draft}
        setDraft={setDraft}
        forms={forms}
        bookingPages={bookingPages}
        onSaved={() => { wizard.onClose(); onChanged(); }}
      />

      <AlertDialog isOpen={!!pendingDelete} leastDestructiveRef={cancelRef} onClose={() => setPendingDelete(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete QR code</AlertDialogHeader>
            <AlertDialogBody>
              Delete <b>{pendingDelete?.label}</b>? Any printed copies will show a "link unavailable" page,
              and its scan history will be removed. This can't be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setPendingDelete(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={deleting}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
